package com.dotdrives.web

import com.dotdrives.web.ui.useFlowState
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import kotlinx.serialization.encodeToString
import react.FC
import react.Key
import react.Props
import react.useEffect
import react.useState

internal external interface PublicGivingCompositionProps : Props {
    var hash: String
}

internal val PublicGivingComposition =
    FC<PublicGivingCompositionProps> { props ->
        val route = parsePublicGivingRoute(props.hash)
        if (route == null) {
            com.dotdrives.web.pages.giving.PublicGivingUnavailable()
        } else {
            PublicGivingLoaded {
                key = "${route.orgId}:${route.slug}".unsafeCast<Key>()
                this.route = route
            }
        }
    }

private external interface PublicGivingLoadedProps : Props {
    var route: PublicGivingRoute
}

private val PublicGivingLoaded =
    FC<PublicGivingLoadedProps> { props ->
        val route = props.route
        val controller = usePublicGivingController(route)
        val state = useFlowState(controller.state)
        useEffect(state.checkoutUrl) {
            state.checkoutUrl?.let {
                if (com.dotdrives.model.safeCheckoutUrl(it)) window.location.assign(it)
            }
        }
        useEffect(state.attempt?.statusToken, route.payment) {
            if (state.attempt?.statusToken != null) {
                repeat(12) {
                    if (
                        controller.state.value.completed ||
                            controller.state.value.paymentStatus == "failed" ||
                            controller.state.value.reconciliationRequired
                    )
                        return@useEffect
                    controller.checkStatus()
                    kotlinx.coroutines.delay(5000)
                }
            }
        }
        com.dotdrives.web.pages.giving.PublicGivingPage {
            this.state = state
            this.controller = controller
            onNewGift = {
                controller.newGift()
                if (controller.state.value.attempt == null)
                    window.location.hash = "#/give/${route.orgId}/${route.slug}"
            }
            paymentReturn = route.payment
            missingReturn =
                route.payment == "return" &&
                    (state.attempt == null ||
                        route.operationId != state.attempt?.request?.operationId)
        }
    }

/** Production storage/restoration boundary, also mounted by the lifecycle regression tests. */
internal fun usePublicGivingController(
    route: PublicGivingRoute,
    repositoryFactory: () -> com.dotdrives.repository.PublicGivingRepository = { com.dotdrives.repository.SupabasePublicGivingRepository() },
    readStored: (String) -> String? = { window.sessionStorage.getItem(it) },
    writeStored: (String, String?) -> Unit = { key, value ->
        if (value == null) window.sessionStorage.removeItem(key)
        else window.sessionStorage.setItem(key, value)
    },
    newOperation: () -> String = { com.dotdrives.core.uuidV7() },
): com.dotdrives.controller.PublicGivingController {
    val (holder) = useState {
        val scope = MainScope()
        val key = "dotdrives.public.attempt:${route.orgId}:${route.slug}"
        val json = kotlinx.serialization.json.Json { ignoreUnknownKeys = true; encodeDefaults = true }
        scope to com.dotdrives.controller.PublicGivingController(
            repositoryFactory(), scope, route.orgId, route.slug,
            newId = newOperation,
            onRevisionReady = { window.location.hash = "#/give/${route.orgId}/${route.slug}" },
            saveAttempt = { writeStored(key, it?.let { attempt -> json.encodeToString(attempt) }) },
            readAttempt = { readStored(key)?.let { json.decodeFromString<com.dotdrives.model.PublicGivingAttempt>(it) } },
        )
    }
    useEffect(holder) {
        holder.second.restore()
        try { awaitCancellation() } finally { holder.first.cancel() }
    }
    return holder.second
}

internal external interface PublicGivingAuthCallbackProps : Props {
    var hash: String
    var onFinished: () -> Unit
}

/** Let Auth consume callback credentials before restoring the full public hash. */
internal val PublicGivingAuthCallback =
    FC<PublicGivingAuthCallbackProps> { props ->
        val auth = useAuthController()
        val state = useFlowState(auth.state)
        useEffect(state.checked) {
            if (state.checked) {
                window.history.replaceState(null, "", "${window.location.pathname}${props.hash}")
                window.asDynamic().dispatchEvent(js("new Event('hashchange')"))
                props.onFinished()
            }
        }
    }
